import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ShieldCheck, Truck, Sparkles, ArrowRight, Star, ChefHat } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney } from '@/lib/realtime';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';

export default function Landing() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get('/menu/items').then((r) => setFeatured((r.data || []).slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero */}
      <section data-testid="landing-hero" className="relative overflow-hidden hero-warm-wash">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:py-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" /> Now serving live ordering
            </div>
            <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              A premium way to dine,
              <br />
              <span className="text-primary">your table or ours.</span>
            </h1>
            <p className="mt-4 max-w-xl text-base leading-7 text-muted-foreground">
              Order from your phone, your table iPad, or your favorite booth. Live tracking, secure checkout, and a kitchen that knows what you want before you sit down.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/menu"><Button data-testid="landing-order-now-button" size="lg" className="h-12 px-6 text-base">Order now <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <Link to="/signup"><Button data-testid="landing-create-account-button" size="lg" variant="secondary" className="h-12 px-6">Create account</Button></Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-5 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-700" /> Secure checkout</span>
              <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-emerald-700" /> Live order tracking</span>
              <span className="inline-flex items-center gap-2"><Star className="h-4 w-4 text-amber-600" /> 4.9 rating from diners</span>
            </div>
          </div>
          <div className="relative">
            <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
              <AspectRatio ratio={4 / 3}>
                <img
                  data-testid="landing-hero-image"
                  src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=80"
                  alt="Signature plated dish"
                  className="h-full w-full object-cover"
                />
              </AspectRatio>
            </div>
            <div className="absolute -bottom-4 -left-4 hidden rounded-2xl border border-border bg-card p-3 shadow-lg md:block">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <ChefHat className="h-5 w-5 text-primary" />
                </span>
                <div>
                  <p className="text-sm font-semibold">Avg. prep time</p>
                  <p className="text-xs text-muted-foreground">14 minutes · today</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-semibold sm:text-3xl">Tonight's favorites</h2>
            <p className="text-sm text-muted-foreground">A taste of what's flying out of the kitchen.</p>
          </div>
          <Link to="/menu" data-testid="landing-see-full-menu-link" className="text-sm font-medium text-primary hover:underline">See full menu</Link>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((it) => (
            <Card key={it.id} data-testid="landing-featured-card" className="group overflow-hidden">
              <AspectRatio ratio={4 / 3} className="overflow-hidden">
                <img src={it.image_url} alt={it.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
              </AspectRatio>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-semibold leading-tight">{it.name}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{it.description}</p>
                  </div>
                  <span className="shrink-0 font-semibold tabular">{fmtMoney(it.price)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Roles strip */}
      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
        <Card className="overflow-hidden">
          <div className="grid items-stretch gap-0 md:grid-cols-3">
            <div className="p-6">
              <h3 className="font-display text-lg font-semibold">For customers</h3>
              <p className="mt-2 text-sm text-muted-foreground">Order from anywhere, track in real time, pay securely.</p>
            </div>
            <div className="border-y border-border p-6 md:border-x md:border-y-0">
              <h3 className="font-display text-lg font-semibold">For staff</h3>
              <p className="mt-2 text-sm text-muted-foreground">Kitchen display, POS, and an admin dashboard that just works.</p>
            </div>
            <div className="p-6">
              <h3 className="font-display text-lg font-semibold">For owners</h3>
              <p className="mt-2 text-sm text-muted-foreground">Multi-branch ready with sales insights and audit-friendly logs.</p>
            </div>
          </div>
        </Card>
      </section>

      <Footer />
    </div>
  );
}
